(() => {
  const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

  const debounce = (fn, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const pauseAllMedia = () => {
    document.querySelectorAll('.js-youtube').forEach((video) => {
      video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });
    document.querySelectorAll('.js-vimeo').forEach((video) => {
      video.contentWindow?.postMessage('{"method":"pause"}', '*');
    });
    document.querySelectorAll('video').forEach((video) => video.pause());
    document.querySelectorAll('slib-product-model').forEach((model) => {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    });
  };

  const focusableSelectors = [
    'summary',
    'a[href]',
    'button:enabled',
    '[tabindex]:not([tabindex^="-"])',
    '[draggable]',
    'area',
    'input:not([type=hidden]):enabled',
    'select:enabled',
    'textarea:enabled',
    'object',
    'iframe',
  ];

  const trapFocusHandlers = {};

  const getFocusableElements = (container) =>
    Array.from(container.querySelectorAll(focusableSelectors.join(','))).filter((element) => element.offsetWidth || element.offsetHeight || element === document.activeElement);

  const trapFocus = (container, elementToFocus = container) => {
    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      if (event.target !== container && event.target !== last && event.target !== first) return;
      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = () => {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = (event) => {
      if (event.code.toUpperCase() !== 'TAB') return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }
      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);
    elementToFocus.focus();
  };

  const removeTrapFocus = (elementToFocus = null) => {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
    if (elementToFocus) elementToFocus.focus();
  };

  class SlibSliderComponent extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.enableSliderLooping = false;
      this.currentPageElement = this.querySelector('.slider-counter--current');
      this.pageTotalElement = this.querySelector('.slider-counter--total');
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.hasDots = false;
      this.scrollMultiplier = isRtl ? -1 : 1;

      if (!this.slider || !this.nextButton) return;

      this.slider.addEventListener('scroll', this.update.bind(this));
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
      this.sliderControlWrapper = this.querySelector('.slider-buttons');

      if (this.sliderControlWrapper && this.sliderControlWrapper.querySelector('.slider-counter__link')) {
        this.internalVideos = this.querySelectorAll('slib-internal-video');
        this.pauseVideos = this.dataset.pauseVideos === 'true' && this.internalVideos.length > 0;
        this.sliderFirstItemNode = this.slider.querySelector('.slider__slide');
        this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
        this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
        this.hasDots = true;
      }

      this.initPages();
      this.resizeObserver = new ResizeObserver(() => this.initPages());
      this.resizeObserver.observe(this.slider);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    linkToSlide(event) {
      event.preventDefault();
      const index = this.sliderControlLinksArray.indexOf(event.currentTarget);
      let hiddenBefore = 0;
      for (let i = 0; i < index; i += 1) {
        if (this.sliderControlLinksArray[i].classList.contains('hidden')) hiddenBefore += 1;
      }
      const visibleIndex = index - hiddenBefore;
      const offset = visibleIndex + 1 - this.currentPage;
      const scrollLeft = this.slider.scrollLeft * this.scrollMultiplier;
      const target = scrollLeft + this.sliderFirstItemNode.clientWidth * offset;
      this.slider.scrollTo({ left: target * this.scrollMultiplier, behavior: 'smooth' });
    }

    initPages() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter((item) => item.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) {
        this.prevButton?.setAttribute('disabled', 'disabled');
        this.nextButton?.setAttribute('disabled', 'disabled');
        return;
      }
      const firstOffset = this.sliderItemsToShow[0].offsetLeft * this.scrollMultiplier;
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft * this.scrollMultiplier - firstOffset;
      if (!this.sliderItemOffset) return;
      this.slidesPerPage = Math.max(1, Math.floor((this.slider.clientWidth - firstOffset) / this.sliderItemOffset));
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      this.update();
    }

    resetPages() {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.initPages();
    }

    update() {
      if (!this.slider || !this.nextButton || !this.sliderItemOffset) return;
      const previousPage = this.currentPage;
      this.currentPage = Math.round((this.slider.scrollLeft * this.scrollMultiplier) / this.sliderItemOffset) + 1;
      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }
      if (this.currentPage !== previousPage) {
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }));
      }

      if (this.hasDots) {
        let visibleIndex = 0;
        const currentIndex = this.currentPage - 1;
        this.sliderControlLinksArray.forEach((link) => {
          link.classList.remove('slider-counter__link--active');
          link.removeAttribute('aria-current');
          if (!link.classList.contains('hidden')) {
            if (visibleIndex === currentIndex) {
              link.classList.add('slider-counter__link--active');
              link.setAttribute('aria-current', 'true');
            }
            visibleIndex += 1;
          }
        });
      }

      const currentSlide = this.sliderItems[this.currentPage - 1];
      if (this.pauseVideos && currentSlide) {
        this.internalVideos.forEach((video) => {
          if (currentSlide.id === video.closest('[id^="Slide-"]')?.id) return;
          const htmlVideo = video.querySelector('video');
          if (!htmlVideo) return;
          if (video.dataset.autoplay === 'true') {
            htmlVideo.muted = true;
            video.classList.add('internal-video--muted');
          } else {
            htmlVideo.pause();
            video.classList.remove('internal-video--playing');
          }
        });
      }

      if (this.enableSliderLooping) return;
      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }
      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1], true)) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }

    isSlideVisible(element, partial = false, offset = 0) {
      if (!element) return false;
      const lastVisible = element.offsetLeft * this.scrollMultiplier + element.clientWidth;
      const viewport = this.slider.scrollLeft * this.scrollMultiplier + this.slider.clientWidth + (partial ? 10 : 0) - offset;
      return lastVisible <= viewport;
    }

    onButtonClick(event) {
      event.preventDefault();
      const step = Number(event.currentTarget.dataset.step || 1);
      const current = this.slider.scrollLeft * this.scrollMultiplier;
      const target = event.currentTarget.name === 'next' ? current + step * this.sliderItemOffset : current - step * this.sliderItemOffset;
      this.slider.scrollTo({ left: target * this.scrollMultiplier });
    }
  }

  class SlibModalDialog extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false));
      this.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'mouse') return;
        if (event.target.closest('slib-deferred-media, slib-product-model')) return;
        this.hide();
      });
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener) {
      this.openedBy = opener;
      document.body.classList.add('overflow-hidden');
      document.body.style.overflow = 'hidden';
      this.setAttribute('open', '');
      trapFocus(this, this.querySelector('[role="dialog"]'));
      pauseAllMedia();
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      document.body.style.overflow = '';
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      removeTrapFocus(this.openedBy);
      pauseAllMedia();
    }
  }

  class SlibProductModal extends SlibModalDialog {
    show(opener) {
      super.show(opener);
      this.showActiveMedia();
    }

    showActiveMedia() {
      const mediaId = this.openedBy?.getAttribute('data-media-id');
      this.querySelectorAll(`[data-media-id]:not([data-media-id="${mediaId}"])`).forEach((element) => {
        element.classList.remove('active');
      });
      const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;
      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
      activeMedia.classList.add('active');
      activeMedia.scrollIntoView();

      const container = this.querySelector('[role="document"]');
      if (container && activeMedia.width) container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

      if (activeMedia.nodeName === 'SLIB-DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube')) {
        activeMedia.loadContent();
      }
    }
  }

  class SlibModalOpener extends HTMLElement {
    constructor() {
      super();
      const button = this.querySelector('button:not(.internal-video__play, .internal-video__sound-btn)');
      if (!button) return;
      button.addEventListener('click', () => {
        const modal = document.querySelector(this.getAttribute('data-modal'));
        if (modal) modal.show(button);
      });
    }
  }

  class SlibDeferredMedia extends HTMLElement {
    constructor() {
      super();
      const poster = this.querySelector('[id^="Deferred-Poster-"]');
      if (!poster) return;
      poster.addEventListener('click', this.loadContent.bind(this));
    }

    loadContent(focus = true) {
      pauseAllMedia();
      if (this.getAttribute('loaded')) return;
      const template = this.querySelector('template');
      if (!template) return;
      const wrapper = document.createElement('div');
      wrapper.appendChild(template.content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
      const media = this.appendChild(wrapper.querySelector('video, model-viewer, iframe'));
      if (focus && media) media.focus();
    }
  }

  class SlibProductModel extends SlibDeferredMedia {
    loadContent() {
      super.loadContent();
      if (!window.Shopify?.loadFeatures) return;
      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    }

    setupModelViewerUI(errors) {
      if (errors || !window.Shopify?.ModelViewerUI) return;
      const modelViewer = this.querySelector('model-viewer');
      if (modelViewer) this.modelViewerUI = new window.Shopify.ModelViewerUI(modelViewer);
    }
  }

  const ProductModelXR = {
    loadShopifyXR() {
      if (!window.Shopify?.loadFeatures) return;
      window.Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: this.setupShopifyXR.bind(this),
        },
      ]);
    },

    setupShopifyXR(errors) {
      if (errors) return;
      if (!window.ShopifyXR) {
        document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR());
        return;
      }
      document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
        window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
        modelJSON.remove();
      });
      window.ShopifyXR.setupXRElements();
    },
  };

  class SlibInternalVideo extends HTMLElement {
    constructor() {
      super();
      this.playButton = this.querySelector('.internal-video__play');
      this.soundButton = this.querySelector('.internal-video__sound-btn');
      this.video = this.querySelector('video');
      this.source = this.video?.querySelector('source[data-src]');
      this.timeline = this.querySelector('.internal-video__timeline');
      this.noPlayBtn = this.dataset.noPlayBtn === 'true';
      this.dragging = false;

      this.source?.remove();
      this.playButton?.addEventListener('click', this.togglePlay.bind(this));
      this.soundButton?.addEventListener('click', this.toggleSound.bind(this));
      this.video?.addEventListener('ended', this.endedVideo.bind(this));
      this.video?.addEventListener('waiting', this.showSpinner.bind(this));
      this.video?.addEventListener('canplaythrough', this.hideSpinner.bind(this));
      this.video?.addEventListener('playing', this.hideSpinner.bind(this));

      if (this.timeline && this.video) {
        this.video.addEventListener('timeupdate', this.updateTimeline.bind(this));
        this.timeline.addEventListener('click', this.seekVideo.bind(this));
        this.timeline.addEventListener('mousedown', this.startDrag.bind(this));
        this.timeline.addEventListener('touchstart', this.startDrag.bind(this));
        document.addEventListener('mouseup', this.stopDrag.bind(this));
        document.addEventListener('touchend', this.stopDrag.bind(this));
        document.addEventListener('mousemove', this.handleDrag.bind(this));
        document.addEventListener('touchmove', this.handleDrag.bind(this));
      }

      if (this.dataset.autoplay === 'true' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
          root: null,
          rootMargin: '0px',
          threshold: 0.05,
        });
        this.observer.observe(this);
      }
    }

    togglePlay() {
      if (!this.video) return;
      if (this.video.paused) {
        if (!this.suspended) {
          this.video.load();
          this.suspended = true;
        }
        this.classList.add('internal-video--playing');
        if (this.playButton && this.noPlayBtn) this.playButton.style.visibility = 'hidden';
        const promise = this.video.play();
        if (promise !== undefined) {
          promise.catch(() => {
            this.classList.remove('internal-video--playing');
            if (this.playButton && this.noPlayBtn) this.playButton.style.visibility = 'visible';
          });
        }
      } else {
        this.video.pause();
        this.classList.remove('internal-video--playing');
      }
    }

    endedVideo() {
      this.classList.remove('internal-video--playing');
    }

    toggleSound() {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
      this.classList.toggle('internal-video--muted', this.video.muted);
    }

    updateTimeline() {
      if (!this.video?.duration) return;
      this.style.setProperty('--completed', `${(this.video.currentTime / this.video.duration) * 100}%`);
    }

    seekVideo(event) {
      if (!this.video || !this.timeline) return;
      const pointer = event.touches ? event.touches[0] : event;
      const rect = this.timeline.getBoundingClientRect();
      const position = (pointer.clientX - rect.left) / rect.width;
      this.video.currentTime = position * this.video.duration;
    }

    startDrag(event) {
      event.preventDefault();
      this.dragging = true;
      this.seekVideo(event);
    }

    stopDrag() {
      this.dragging = false;
    }

    handleDrag(event) {
      if (!this.dragging) return;
      this.seekVideo(event);
    }

    showSpinner() {
      this.classList.add('internal-video--loading');
    }

    hideSpinner() {
      this.classList.remove('internal-video--loading');
    }

    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || !this.video) return;
        for (const source of this.video.querySelectorAll('source[data-src]')) {
          source.setAttribute('src', source.getAttribute('data-src'));
          source.removeAttribute('data-src');
        }
        this.video.load();
        const promise = this.video.play();
        if (promise !== undefined) {
          promise.catch((error) => {
            if (error.name === 'NotAllowedError') {
              this.classList.remove('internal-video--playing');
              if (this.playButton && this.noPlayBtn) this.playButton.style.visibility = 'visible';
            }
          }).then(() => {
            this.video.play();
          });
        }
        this.observer.disconnect();
      });
    }
  }

  class SlibMediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
      };
      this.mql = window.matchMedia('(min-width: 46.875rem)');
      this.prependMedia = this.dataset.disablePrepend !== 'true';
      this.filteringOption = this.dataset.filteringOption;
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button')?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const current = event.detail.currentElement;
      if (!current) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach((slide) => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        });
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => element.classList.remove('is-active'));
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          activeThumbnail?.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.resetPages) this.elements.viewer.resetPages();
      }

      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      });

      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        this.elements.viewer?.initPages?.();
        this.elements.viewer?.update?.();
        this.elements.thumbnails?.initPages?.();
        this.elements.thumbnails?.update?.();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;
      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
      if (this.elements.thumbnails.isSlideVisible?.(thumbnail, false, 10)) return;
      this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image || !this.elements.liveRegion) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', 'false');
        this.elements.liveRegion.textContent = `Image ${position} is now available in gallery view`;
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'true');
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
    }
  }

  class SlibProductGallerySection extends HTMLElement {
    connectedCallback() {
      this.gallery = this.querySelector('slib-media-gallery');
      this.variantData = this.readVariantData();
      this.sectionId = this.dataset.section;
      this.boundChange = this.onDocumentChange.bind(this);
      this.boundVariantEvent = this.onVariantEvent.bind(this);
      document.addEventListener('change', this.boundChange);
      document.addEventListener('variant:change', this.boundVariantEvent);
      document.addEventListener('variant-change', this.boundVariantEvent);
      document.addEventListener('variantChange', this.boundVariantEvent);
      this.enableZoomOnHover(2);
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.boundChange);
      document.removeEventListener('variant:change', this.boundVariantEvent);
      document.removeEventListener('variant-change', this.boundVariantEvent);
      document.removeEventListener('variantChange', this.boundVariantEvent);
    }

    readVariantData() {
      const script = this.querySelector('[data-slib-product-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch {
        return [];
      }
    }

    onDocumentChange(event) {
      const input = event.target.closest('[name="id"]');
      if (!input) return;
      const variant = this.variantData.find((item) => String(item.id) === String(input.value));
      if (variant) this.updateVariantMedia(variant);
    }

    onVariantEvent(event) {
      const variant = event.detail?.variant || event.detail?.currentVariant || event.detail;
      if (!variant) return;
      const variantId = variant.id;
      const localVariant = this.variantData.find((item) => String(item.id) === String(variantId)) || variant;
      this.updateVariantMedia(localVariant);
    }

    updateVariantMedia(variant) {
      const mediaId = variant.featured_media_id || variant.featured_media?.id;
      if (!mediaId || !this.gallery) return;
      this.gallery.setActiveMedia(`${this.sectionId}-${mediaId}`, true, Boolean(this.gallery.filteringOption), variant);
    }

    enableZoomOnHover(zoomRatio) {
      this.querySelectorAll('.image-magnify-hover').forEach((image) => {
        image.addEventListener('click', (event) => {
          const overlay = document.createElement('div');
          overlay.setAttribute('class', 'image-magnify-full-size');
          overlay.setAttribute('aria-hidden', 'true');
          overlay.style.backgroundImage = `url('${image.src}')`;
          image.parentElement.insertBefore(overlay, image);
          const move = (moveEvent) => {
            const ratio = image.height / image.width;
            const container = moveEvent.target.getBoundingClientRect();
            const xPosition = moveEvent.clientX - container.left;
            const yPosition = moveEvent.clientY - container.top;
            const xPercent = `${xPosition / (overlay.clientWidth / 100)}%`;
            const yPercent = `${yPosition / ((overlay.clientWidth * ratio) / 100)}%`;
            overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
            overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
          };
          overlay.addEventListener('click', () => overlay.remove());
          overlay.addEventListener('mousemove', move);
          overlay.addEventListener('mouseleave', () => overlay.remove());
          move(event);
        });
      });
    }
  }

  if (!customElements.get('slib-slider-component')) customElements.define('slib-slider-component', SlibSliderComponent);
  if (!customElements.get('slib-modal-opener')) customElements.define('slib-modal-opener', SlibModalOpener);
  if (!customElements.get('slib-deferred-media')) customElements.define('slib-deferred-media', SlibDeferredMedia);
  if (!customElements.get('slib-product-model')) customElements.define('slib-product-model', SlibProductModel);
  if (!customElements.get('slib-product-modal')) customElements.define('slib-product-modal', SlibProductModal);
  if (!customElements.get('slib-media-gallery')) customElements.define('slib-media-gallery', SlibMediaGallery);
  if (!customElements.get('slib-internal-video')) customElements.define('slib-internal-video', SlibInternalVideo);
  if (!customElements.get('slib-product-gallery-section')) customElements.define('slib-product-gallery-section', SlibProductGallerySection);

  window.addEventListener('DOMContentLoaded', () => {
    ProductModelXR.loadShopifyXR();
  });
})();
